import { CostModel, createPAndICostModel } from '@ownup/fin-lib';
import { annualizedPercentFactor, conformingLoanLimit } from '../constants';
import { AffordamilityState } from '../types';
import { pmiTable } from './pmi';

const getInsuranceCostModel = (downPayment: AffordamilityState['downPayment']) => [
  {
    presentValueFactor: 0,
    costOffset: 100,
    minPresentValue: 0,
    maxPresentValue: 400_000 - downPayment
  },
  {
    presentValueFactor: 0,
    costOffset: 125,
    minPresentValue: 0,
    maxPresentValue: 600_000 - downPayment
  },
  {
    presentValueFactor: 0,
    costOffset: 150,
    minPresentValue: 0,
    maxPresentValue: Number.POSITIVE_INFINITY
  }
];

const getPAndICostModel = (initialRate: AffordamilityState['interestRate']) =>
  createPAndICostModel({ initialRate, loanTerm: 30 });

const getPmiCostModel = (
  downPayment: AffordamilityState['downPayment'],
  creditScore: AffordamilityState['creditScore']
): CostModel[] => [
  ...pmiTable[creditScore as keyof typeof pmiTable]!.map(({ pmiRate, maxLTV }) => ({
    presentValueFactor: pmiRate / annualizedPercentFactor,
    costOffset: 0,
    minPresentValue: 0,
    maxPresentValue: Math.min((maxLTV * downPayment) / (1 - maxLTV), conformingLoanLimit) // PMI not available on non-conforming loans
  })),
  {
    presentValueFactor: 0,
    costOffset: 0,
    minPresentValue: 0,
    maxPresentValue: 4 * downPayment // .8 LTV
  }
];

const getTaxesCostModel = (
  downPayment: AffordamilityState['downPayment'],
  taxRate: AffordamilityState['propertyTaxRate']
) => {
  const fractionalTaxRate = taxRate / annualizedPercentFactor;
  return [
    {
      presentValueFactor: fractionalTaxRate,
      costOffset: downPayment * fractionalTaxRate,
      minPresentValue: 0,
      maxPresentValue: Number.POSITIVE_INFINITY
    }
  ];
};
const getHoaFeesCostModel = (hoaFees: AffordamilityState['hOAFees']) => [
  {
    presentValueFactor: 0,
    costOffset: hoaFees,
    minPresentValue: 0,
    maxPresentValue: Number.POSITIVE_INFINITY
  }
];
export const getCostModels = (state: AffordamilityState) => ({
  insurance: getInsuranceCostModel(state.downPayment),
  pAndI: getPAndICostModel(state.interestRate),
  pmi: getPmiCostModel(state.downPayment, state.creditScore),
  taxes: getTaxesCostModel(state.downPayment, state.propertyTaxRate),
  hoaFees: getHoaFeesCostModel(state.hOAFees)
});
